/**
 * The user-configurable accent colour ("Maple Night" design language — docs/DESIGN.md).
 *
 * The whole app is tinted from a single CSS custom property, `--accent`, declared in `index.css`
 * with the default maple amber. Every other accent tint (`--accent-strong`, `--accent-dim`,
 * `--accent-border`) derives from it via `color-mix`, so overriding the one property on `<html>`
 * recolours the shell glow, nav, stamps and buttons together. `--accent-contrast` (the text colour
 * used ON accent-filled surfaces) stays fixed at the dark ink from index.css — every preset below
 * clears 6:1+ against it, so there is no need to swap in a light alternative per accent the way a
 * literal light/dark chooser would.
 *
 * Persistence is localStorage-only for now (DESIGN.md: "server-side persistence is a later
 * nicety"); `applyStoredAccent` re-applies it at boot, before the first paint, so there's no flash
 * of the default amber for a user who picked something else.
 */

export const DEFAULT_ACCENT = '#ffa02e';

/**
 * `--accent-contrast` (index.css): the fixed dark-ink text colour used ON accent-filled surfaces
 * (buttons, the active nav pill, the skip link, the stamp fill). It does not vary per theme (see
 * docs/specs/REV10-themes.md) -- every preset below, and any custom colour a user picks, is
 * checked against this one constant, which is why it's exported rather than read back from the
 * DOM at render time.
 */
export const ACCENT_CONTRAST = '#191006';

/**
 * The twelve curated swatches offered on /settings (docs/DESIGN.md, expanded from six by REV10).
 * The original six keep their exact names/values -- users have them stored in localStorage. Every
 * swatch clears >=6:1 against `ACCENT_CONTRAST` (verified in tokens.contrast.test.ts, not
 * eyeballed).
 */
export const ACCENT_PRESETS: ReadonlyArray<{ name: string; color: string }> = [
  { name: 'Maple Amber', color: DEFAULT_ACCENT },
  { name: 'Leaf Crimson', color: '#ff5d47' },
  { name: 'Mushroom Coral', color: '#ff7d6b' },
  { name: 'Slime Lime', color: '#9ed44a' },
  { name: 'Sky Blue', color: '#5fb4ff' },
  { name: 'Pink Bean', color: '#ff7ab0' },
  { name: 'Violet Mushroom', color: '#b18cff' },
  { name: 'Cyan Slime', color: '#3ecfd8' },
  { name: 'Gold Leaf', color: '#e8c13a' },
  { name: 'Vermilion', color: '#ff6a3d' },
  { name: 'Jade Mint', color: '#4fd4a0' },
  { name: 'Magenta Bean', color: '#ff5fb8' },
];

const STORAGE_KEY = 'hypermaple.accent';

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

export function isHexColor(value: string): boolean {
  return HEX_COLOR.test(value);
}

/** Applies an accent to `<html>` and remembers it for the next load. `null` resets to the default. */
export function applyAccent(color: string | null): void {
  const root = document.documentElement;
  const isOverride = color != null && isHexColor(color) && color.toLowerCase() !== DEFAULT_ACCENT;

  if (isOverride) {
    const normalized = color.toLowerCase();
    root.style.setProperty('--accent', normalized);
    try {
      localStorage.setItem(STORAGE_KEY, normalized);
    } catch {
      // Storage can be unavailable (private mode) -- the accent still applies for this page load.
    }
  } else {
    root.style.removeProperty('--accent');
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore, same as above.
    }
  }
}

/** Reads back whatever accent is currently applied (falls back to the default). */
export function currentAccent(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored != null && isHexColor(stored)) {
      return stored;
    }
  } catch {
    // Ignore.
  }
  return DEFAULT_ACCENT;
}

/** Re-applies the accent remembered from the previous session. Called once at startup, before render. */
export function applyStoredAccent(): void {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    return;
  }
  if (stored != null && isHexColor(stored)) {
    applyAccent(stored);
  }
}
